import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    FormLabel,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { mdiAlertCircle } from '@mdi/js';
import { useDispatch, useSelector } from 'react-redux';
import Documents from '../_parts/Documents';
import { IAppReduxStore } from '../../../../redux/store';
import {
    IProjectDocumentAddNoteFields,
    getProjectDocumentsAddNote,
    openAddNoteDialog,
} from '../../../../redux/project/documents/sliceProjectDocumentAddNote';
import CtaButton from '../_parts/CtaButton';
import { vars, headingFontBold, bodyFont, labelFontSemiBold } from '../../../../Theme';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import { fetchViewDataAndDocuments } from '../../../../util/ProjectUtils';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import DialogFrame from '../_parts/DialogFrame';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../../util/ImportUtils';
import DialogHeader from '../_parts/DialogHeader';
import RadioBtnGroup from '../_parts/RadioBtnGroup';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import Message from '../_parts/Message';
import { ContentLayout } from '../_parts/ContentLayout';
import DocStatusIcon from '../../Icons/DocStatusIcon';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

/** TextField styles. */
const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },
    marginBottom: 1,
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const initialInputValues: IProjectDocumentAddNoteFields = {
    subject: '',
    noteComment: '',
    visibility: 'View_DocStatusEdit_NotReleased',
};

const initialErrorStates: { [P in keyof IProjectDocumentAddNoteFields]?: boolean } = {
    subject: false,
};

const helperText = (text: string) => {
    return (
        <Message
            text={text}
            icon={mdiAlertCircle}
            iconSize={0.75}
            color={vars.errorDialogRed}
            typographySx={{
                fontWeight: 400,
                fontSize: '12px',
                ...bodyFont,
            }}
            wrapperSx={{
                border: 'none',
                p: 0,
                m: 0,
                gap: 0.5,
            }}
        />
    );
};

export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'noteDialog' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    const params = useParams();

    const { areaName, replicaId } = params;

    /** Access relevant state variables from the redux store. */
    const { data: selectedDocs, loading: loadingInfoDocs } = useSelector(
        (store: IAppReduxStore) => store.projectDocuments.info,
    );
    const { isNoteAdded, loading, isDialogOpen } = useSelector(
        (store: IAppReduxStore) => store.documentAddNote,
    );

    const [inputs, setInputs] = useState<IProjectDocumentAddNoteFields>(initialInputValues);
    const [showError, setShowError] = useState(initialErrorStates);

    const selecetedDocsUniversalIds = selectedDocs.map(document => document.universalId);

    useEffect(() => {
        if (!isNoteAdded) {
            return;
        }

        dispatch(
            updateSelectedDocuments({
                replicaId,
                areaName,
                documentIds: [],
            }),
        );

        if (areaName === 'import') {
            updateImportDocuments();
        } else {
            fetchViewDataAndDocuments(params);
        }

        handleClose();
        enqueueSnackbar(
            t('toastSuccess', {
                numberOfDocuments: selectedDocs?.length,
            }),
            {
                severity: ToastSeverity.Success,
            },
        );
    }, [isNoteAdded]);

    /** Function to refresh import area data. */
    const updateImportDocuments = () => {
        fetchImportViews(replicaId);
        fetchImportViewData(replicaId);
        fetchImportDocTypes(replicaId);
    };

    /** Function to handle add note when Add button is clicked. */
    const handleAddNote = () => {
        dispatch(
            getProjectDocumentsAddNote({
                replicaId: params.replicaId,
                universalIds: selecetedDocsUniversalIds,
                formData: inputs,
            }),
        );
    };

    /** Function to handle onChange event on form fields. */
    const handleChange = event => {
        setShowError({ ...showError, [event.target.name]: false });
        setInputs({
            ...inputs,
            [event.target.name]: event.target.value,
        });
    };

    /** Function to check if input field is empty to show the error message. */
    const handleErrorState = () => {
        setShowError({
            subject: inputs.subject === '',
        });
        if (inputs.subject !== '') {
            handleAddNote();
        }
    };

    /* Function to reset to the initial states */
    const resetToInitialState = () => {
        setInputs(initialInputValues);
        setShowError(initialErrorStates);
    };

    /* Close the dialog with taking the initial states */
    const handleClose = () => {
        resetToInitialState();
        dispatch(openAddNoteDialog(false));
    };

    /* Dispatched the function to remove documents */
    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={handleClose} dialogTitle={t('addNote')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs}
                    documentSelection={
                        <Documents documents={selectedDocs} removeDocument={removeDocument} />
                    }
                    main={
                        <Stack>
                            <FormControl required>
                                <FormLabel sx={formLabelSx}>{t('noteSubject')}</FormLabel>
                                <TextField
                                    sx={textFieldSx}
                                    name='subject'
                                    value={inputs.subject || ''}
                                    placeholder={t('enterText')}
                                    error={showError.subject}
                                    helperText={showError.subject && helperText(t('errorSubject'))}
                                    onChange={handleChange}
                                    size='small'
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel sx={formLabelSx}>{t('noteComment')}</FormLabel>
                                <TextField
                                    sx={textFieldSx}
                                    name='noteComment'
                                    value={inputs.noteComment || ''}
                                    placeholder={t('enterText')}
                                    multiline
                                    size='small'
                                    onChange={handleChange}
                                />
                            </FormControl>
                            <RadioBtnGroup
                                horizontal
                                label={t('visibility')}
                                value={inputs.visibility}
                                options={[
                                    {
                                        key: 'View_DocStatusEdit_NotReleased',
                                        label: (
                                            <DocStatusIcon
                                                type={'View_DocStatusEdit_NotReleased'}
                                                withLabel={true}
                                                labelSx={{
                                                    ...labelFontSemiBold,
                                                    fontWeight: 500,
                                                }}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'View_DocStatusEdit_Released',
                                        label: (
                                            <DocStatusIcon
                                                type={'View_DocStatusEdit_Released'}
                                                withLabel={true}
                                                labelSx={{
                                                    ...labelFontSemiBold,
                                                    fontWeight: 500,
                                                }}
                                            />
                                        ),
                                    },
                                ]}
                                onSelect={key =>
                                    handleChange({ target: { name: 'visibility', value: key } })
                                }
                            />
                        </Stack>
                    }
                ></ContentLayout>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ paddingRight: '24px' }}>
                <CtaButton
                    variant='text'
                    text={t('cancel')}
                    onClick={handleClose}
                    disabled={loading}
                />

                <CtaButton
                    variant='contained'
                    text={t('add')}
                    onClick={e => {
                        e.preventDefault();
                        handleErrorState();
                    }}
                    disabled={!selectedDocs?.length || loading}
                />
            </DialogActions>
        </DialogFrame>
    );
};
